import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { Diya, Potli, Rangoli } from "./motifs";
import { wa } from "@/lib/site";

const PRODUCTS = [
    {
        name: "Bandhani Aasan Mats",
        tag: "Pooja & Mandir Essentials",
        copy: "Vibrant Bandhani-style aasan mats to seat your deities and dress your home mandir. Available in two sizes — 20″×20″ and 20″×40″ — and three beautiful colourways, they're perfect for daily pooja or festive dressing.",
        Motif: Diya,
        accent: "#BE123C",
        chips: ['20″×20″', '20″×40″', "3 colourways"],
    },
    {
        name: "Decorative Bajot / Chowki",
        tag: "Pooja & Mandir Essentials",
        copy: "A beautiful platform for idols, sweets or festive styling — finished with rich fabric, delicate mirror work and gold detailing. Limited stock, so message us early to check availability.",
        Motif: Rangoli,
        accent: "#E2BA03",
        chips: ["Fabric finish", "Mirror work", "Gold detailing", "Limited stock"],
    },
    {
        name: "Festive Potli Bags",
        tag: "Gifting",
        copy: "Ornate Bandhani-print potlis with brocade panels, gold embroidery and pearl handles. Crafted for weddings, return gifts and every moment worth giving beautifully.",
        Motif: Potli,
        accent: "#047857",
        chips: ["Bandhani print", "Brocade panels", "Pearl handles", "Return gifts"],
    },
];

const FeaturedRow = ({ product, index }) => {
    const flip = index % 2 === 1;
    const { name, tag, copy, Motif, accent, chips } = product;
    return (
        <div
            className={`flex flex-col ${flip ? "lg:flex-row-reverse" : "lg:flex-row"} items-center gap-10 lg:gap-20`}
            data-testid={`featured-product-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.94, y: 40 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                viewport={{ once: true, margin: "-80px" }}
                className="relative w-full lg:w-1/2"
            >
                <div
                    className="relative aspect-[4/3] rounded-[2rem] border border-[#472500]/15 overflow-hidden grid place-items-center"
                    style={{ backgroundColor: "#F7F4EC" }}
                >
                    <div
                        className="absolute inset-0 opacity-[0.06]"
                        style={{
                            backgroundImage: `radial-gradient(${accent} 1px, transparent 1px)`,
                            backgroundSize: "22px 22px",
                        }}
                    />
                    <Motif className="w-1/2 max-w-[260px] opacity-70" style={{ color: accent }} />
                    <span
                        className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.28em] whitespace-nowrap"
                        style={{ color: `${accent}` }}
                    >
                        Product photography coming soon
                    </span>
                    <span
                        className="absolute top-5 left-5 font-display italic text-lg"
                        style={{ color: accent }}
                    >
                        0{index + 1}
                    </span>
                </div>
            </motion.div>

            <div className="w-full lg:w-1/2">
                <Reveal>
                    <p className="text-xs uppercase tracking-[0.3em] font-medium" style={{ color: accent }}>
                        {tag}
                    </p>
                </Reveal>
                <Reveal delay={0.08}>
                    <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-4 text-[#472500] leading-tight">
                        {name}
                    </h3>
                </Reveal>
                <Reveal delay={0.16}>
                    <p className="mt-6 text-base sm:text-lg text-[#472500]/70 leading-relaxed max-w-lg">{copy}</p>
                </Reveal>
                <Reveal delay={0.22}>
                    <div className="mt-6 flex flex-wrap gap-2.5">
                        {chips.map((c) => (
                            <span
                                key={c}
                                className="text-xs font-medium rounded-full border border-[#472500]/25 px-4 py-1.5 text-[#472500]/80"
                            >
                                {c}
                            </span>
                        ))}
                    </div>
                </Reveal>
                <Reveal delay={0.28}>
                    <div className="mt-9 flex flex-wrap items-center gap-5">
                        <a
                            href={wa(`Hi Festive Theory! I'd love to enquire about the ${name}.`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`featured-whatsapp-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="group inline-flex items-center gap-2.5 rounded-full bg-[#472500] text-[#F0EEE8] font-semibold px-7 py-3.5 text-sm transition-all duration-300 hover:bg-[#E2BA03] hover:text-[#472500] hover:-translate-y-0.5"
                        >
                            <MessageCircle size={16} />
                            Enquire on WhatsApp
                        </a>
                        <span className="text-xs uppercase tracking-[0.25em] text-[#472500]/50">
                            Enquire for price
                        </span>
                    </div>
                </Reveal>
            </div>
        </div>
    );
};

const Featured = () => (
    <section id="featured" data-testid="featured-section" className="relative py-28 sm:py-40 bg-[#EAE6DA]/60">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <SectionHeading
                eyebrow="A Closer Look"
                title={<>Featured <em className="italic text-[#E2BA03] [text-shadow:0_1px_0_#472500]">Pieces</em></>}
                testId="featured-heading"
                className="mb-20 sm:mb-28"
            />
            <div className="flex flex-col gap-24 sm:gap-36">
                {PRODUCTS.map((p, i) => (
                    <FeaturedRow key={p.name} product={p} index={i} />
                ))}
            </div>
        </div>
    </section>
);

export default Featured;
